import type { FC } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { isPaidSearchPagesAvailable } from "app/pages/keyword-research/utils/permissions";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { OrganicPaid, TrendCell } from "app/components/React/Table/cells";
import { DefaultCellHeader } from "app/components/React/Table/headerCells";
import { SWReactTable } from "app/components/React/Table/SWReactTable";
import { THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { minAbbrNumberFilter } from "app/filters/ngFilters";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";

import {
    ClicksTrendContainer,
    ClicksTrendValueContainer,
    TrendContainerStyled,
} from "./TopCompetitorsStyledComponents";
const getTableColumns = (params, tab, isWebsite = false) => {
    return [
        {
            field: "Competitor",
            displayName: translate(
                isWebsite
                    ? "keyword.analysis.overview.page.v2.top_competitors.table.column.websites"
                    : "keyword.analysis.overview.page.v2.top_competitors.table.column.pages",
            ),
            cellComponent: (props) => {
                return (
                    <CoreWebsiteCell
                        domain={props.row.Competitor}
                        icon={props.row.Favicon}
                        internalLink={proNavigator.href("organicsearch_websites_v2_traffic_keywords", {
                            ...params,
                            key: props.row.Competitor,
                            pageFilter: JSON.stringify([
                                { url: props.row.Competitor, searchType: `${props.row.Url ? "exact" : "domain"}` },
                            ]),
                            selectedPageTab:
                                !isPaidSearchPagesAvailable() &&
                                params.selectedPageTab === EKeywordTabs[EKeywordTabs.Paid]
                                    ? EKeywordTabs[EKeywordTabs.Total]
                                    : params.selectedPageTab,
                        })}
                    />
                );
            },
            minWidth: 150,
            cellClass: "website-cell",
            columnClass: "website-column",
        },
        {
            field: "Clicks",
            displayName: translate("keyword.analysis.overview.page.v2.top_competitors.table.column.clicks"),
            cellComponent: (props) => {
                const trendCellData = Object.entries(props.row.YearlyTrend).map(([key, value]) => ({
                    key,
                    value,
                }));
                return (
                    <ClicksTrendContainer>
                        <ClicksTrendValueContainer>
                            {minAbbrNumberFilter()(props.value, THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE)}
                        </ClicksTrendValueContainer>
                        <TrendCell
                            {...props}
                            value={trendCellData}
                            Container={TrendContainerStyled}
                        />
                    </ClicksTrendContainer>
                );
            },
            width: 250,
            columnClass: "clicks-column",
        },
        {
            field: "Organic",
            displayName: translate("keyword.analysis.overview.page.v2.top_competitors.table.column.organic_vs_paid"),
            cellComponent: OrganicPaid,
            width: 150,
            lastColumnFixed: true,
        },
    ].map((col) => {
        return {
            ...col,
            headerComponent: DefaultCellHeader,
            sortable: false,
            visible: true,
            disableHeaderCellHover: true,
            isResizable: false,
        };
    });
};

interface ITopCompetitorsTable {
    tableData: any;
    isLoading: boolean;
    tab: EKeywordTabs;
    isWebsite?: boolean;
}

export const TopCompetitorsTable: FC<ITopCompetitorsTable> = ({ tableData, isLoading, tab, isWebsite = false }) => {
    const { params } = useCurrentStateAndParams();
    return (
        <SWReactTable
            tableColumns={getTableColumns(params, tab, isWebsite)}
            tableData={tableData}
            isLoading={isLoading}
            loaderRowsNumber={5}
            disableRowHover={true}
        />
    );
};
